import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownIcon, ArrowUpIcon, EllipsisIcon, PencilIcon, PlusIcon, PowerIcon } from 'lucide-react';
import { Button, DataTable, ErrorNotice, Field, LoadingState, Pill, SearchInput, TextInput, type DataTableSort } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { createContactType, listContactTypes, updateContactType, type ContactType } from '@/lib/contact-types';

const SORT_FIELDS = { label: 'label', key: 'key', order: 'order' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.order, direction: 'asc' };

/**
 * Settings → Contact types: the kinds of outside person an event can carry
 * (client, venue, charity, DMC, AV…). Rename, reorder, enable or disable;
 * disabled types stay on the contacts that already use them.
 *
 * @returns The tab.
 */
export function ContactTypesTab(): React.ReactElement {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ContactType | null>(null);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);

  const types = useQuery({ queryKey: ['contact-types'], queryFn: ({ signal }) => listContactTypes(signal) });
  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['contact-types'] });
  }
  const toggle = useMutation({
    mutationFn: (type: ContactType) => updateContactType(type._id, { isActive: !type.isActive }),
    onSuccess: async (updated) => {
      toast.success(`${updated.label} ${updated.isActive ? 'enabled' : 'disabled'}`);
      await refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  // Moving a type swaps its place with its neighbour in the picker's order.
  const byOrder = [...(types.data ?? [])].sort((a, b) => a.order - b.order);
  const move = useMutation({
    mutationFn: async ({ type, delta }: { type: ContactType; delta: -1 | 1 }) => {
      const index = byOrder.findIndex((row) => row._id === type._id);
      const neighbour = byOrder[index + delta];
      if (index < 0 || !neighbour) return;
      // Keep the orders distinct even when the stored numbers have gaps.
      const mine = index + 1 + delta;
      const theirs = index + 1;
      await Promise.all([updateContactType(type._id, { order: mine }), updateContactType(neighbour._id, { order: theirs })]);
    },
    onSuccess: refresh,
    onError: (err) => toast.error(errorMessage(err)),
  });
  const rows = types.data ? sortRows(filterRows([...types.data], search, ['label', 'key']), sort) : [];
  const commandButton = 'grid size-7 place-items-center text-portal-muted hover:bg-portal-bg hover:text-portal-ink disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <div className="mt-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput data-guide="contact-types-search" label="Search contact types" placeholder="Type or key" value={search} onChange={(event) => setSearch(event.target.value)} className="max-w-xs" />
        <span data-guide="contact-types-add" className="ml-auto">
          <Button onClick={() => setAdding(true)}>
            <PlusIcon aria-hidden className="size-4" /> Add type
          </Button>
        </span>
      </div>

      {types.isPending ? (
        <LoadingState label="Loading contact types…" />
      ) : types.isError ? (
        <ErrorNotice title="Contact types unavailable" message={errorMessage(types.error)} />
      ) : (
        <div data-guide="contact-types-table"><DataTable
          headers={[
            { label: 'Type', sortKey: SORT_FIELDS.label },
            { label: 'Key', sortKey: SORT_FIELDS.key },
            { label: 'Order', sortKey: SORT_FIELDS.order },
            'Status',
            'Actions',
          ]}
          visuallyHiddenHeaders={[4]}
          rightAlignedHeaders={[2]}
          sort={sort}
          onSort={onSort}
        >
          {rows.map((type) => (
            <tr key={type._id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small font-bold">{type.label}</td>
              <td className="p-3 font-mono text-portal-small text-portal-muted">{type.key}</td>
              <td className="p-3 text-right">
                {(() => {
                  const position = byOrder.findIndex((row) => row._id === type._id);
                  return (
                    <span data-guide="contact-types-order" className="inline-flex divide-x divide-portal-line overflow-hidden rounded-portal-button border border-portal-line bg-white align-middle" role="group" aria-label={`Order ${type.label}`}>
                      <button type="button" aria-label={`Move ${type.label} up`} disabled={position <= 0 || move.isPending} onClick={() => move.mutate({ type, delta: -1 })} className={commandButton}>
                        <ArrowUpIcon aria-hidden className="size-4" />
                      </button>
                      <button type="button" aria-label={`Move ${type.label} down`} disabled={position < 0 || position >= byOrder.length - 1 || move.isPending} onClick={() => move.mutate({ type, delta: 1 })} className={commandButton}>
                        <ArrowDownIcon aria-hidden className="size-4" />
                      </button>
                    </span>
                  );
                })()}
              </td>
              <td className="p-3 text-portal-small">
                <Pill tone={type.isActive ? 'success' : 'neutral'}>{type.isActive ? 'Active' : 'Disabled'}</Pill>
              </td>
              <td className="p-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" data-guide="contact-types-row-menu" aria-label={`Actions for ${type.label}`} className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink">
                      <EllipsisIcon aria-hidden className="size-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => setEditing(type)}>
                      <PencilIcon aria-hidden className="size-4" /> Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => toggle.mutate(type)}>
                      <PowerIcon aria-hidden className="size-4" /> {type.isActive ? 'Disable' : 'Enable'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </DataTable></div>
      )}

      {adding ? (
        <AddTypeDialog
          nextOrder={(types.data?.length ?? 0) + 1}
          onClose={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false);
            await refresh();
          }}
        />
      ) : null}

      {editing ? (
        <EditTypeDialog
          key={editing._id}
          type={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function EditTypeDialog({ type, onClose, onSaved }: { readonly type: ContactType; readonly onClose: () => void; readonly onSaved: () => Promise<void> }): React.ReactElement {
  const [label, setLabel] = useState(type.label);
  const save = useMutation({
    mutationFn: () => updateContactType(type._id, { label: label.trim() }),
    onSuccess: async () => {
      toast.success(`Saved ${label.trim()}`);
      await onSaved();
    },
  });
  const valid = label.trim().length > 0;
  return (
    <Dialog open onOpenChange={(next) => (!next && !save.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-sm">
        <DialogTitle>Rename contact type</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !save.isPending) save.mutate();
          }}
        >
          <Field guide="contact-type-rename" label="Label" htmlFor="type-label" required>
            <TextInput id="type-label" value={label} maxLength={60} onChange={(event) => setLabel(event.target.value)} />
          </Field>
          <p className="text-portal-micro text-portal-muted">
            Key <span className="font-mono">{type.key}</span> is fixed; contacts reference it.
          </p>
          {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Names a new contact type; the key comes from the label on the API. */
function AddTypeDialog({ nextOrder, onClose, onSaved }: { readonly nextOrder: number; readonly onClose: () => void; readonly onSaved: () => Promise<void> }): React.ReactElement {
  const [label, setLabel] = useState('');
  const create = useMutation({
    mutationFn: () => createContactType({ label: label.trim(), order: nextOrder }),
    onSuccess: async () => {
      toast.success(`Added ${label.trim()}`);
      await onSaved();
    },
  });
  const valid = label.trim().length > 0;
  return (
    <Dialog open onOpenChange={(next) => (!next && !create.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-sm">
        <DialogTitle>Add a contact type</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !create.isPending) create.mutate();
          }}
        >
          <Field guide="contact-type-label" label="Type" htmlFor="new-type-label" hint="As it reads in the contact picker. It goes last; move it with the arrows in the list." required>
            <TextInput id="new-type-label" value={label} placeholder="Photographer" maxLength={60} autoFocus onChange={(event) => setLabel(event.target.value)} />
          </Field>
          {create.isError ? <ErrorNotice inline title="Not added" message={errorMessage(create.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={create.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || create.isPending}>
              {create.isPending ? 'Adding…' : 'Add type'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
